/**
 * src/components/BarberProfile.jsx
 * PRODUCTION BUILD 2026 - ESDRAS MASTER
 * FEATURES: 3D Cranial Solid View, Monnify Split Integration, Real-time Portfolio
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase';
import BookingModal from './booking-modal';
import Chat from './chat';
import toast from 'react-hot-toast';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

// Sub-component for the Verified Badge
const VerifiedBadge = () => (
  <span style={{
    background: 'rgba(184, 134, 11, 0.15)',
    color: GOLD,
    fontSize: '0.65rem',
    padding: '2px 8px',
    borderRadius: '4px',
    border: `1px solid ${GOLD}`,
    marginLeft: '8px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: 'bold'
  }}>
    ✓ Verified Client
  </span>
);

export default function BarberProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [barber, setBarber] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [portfolio, setPortfolio] = useState([]); 
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user3D, setUser3D] = useState(null);

  // Check if current user is the owner of this profile
  const isOwner = auth.currentUser?.uid === id;

  const toastStyle = {
    style: { background: NAVY, color: GOLD, border: `1px solid ${GOLD}`, borderRadius: '12px' }
  };

  useEffect(() => {
    const fetchBarber = async () => {
      try {
        // Checking 'barbers' collection - Primary source for subAccount codes
        const snap = await getDoc(doc(db, 'barbers', id));
        if (snap.exists()) {
          setBarber({ id: snap.id, ...snap.data() });
        } else {
          // Fallback check in users collection
          const userSnap = await getDoc(doc(db, 'users', id));
          if (userSnap.exists()) {
            setBarber({ id: userSnap.id, ...userSnap.data() });
          } else {
            toast.error('Stylist not found', toastStyle);
            navigate('/discovery'); 
          }
        }
      } catch (err) {
        console.error('Error fetching stylist:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserHeadScan = async () => {
      if (auth.currentUser) {
        const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (snap.exists()) {
          const userData = snap.data();
          // [PERSONALIZATION] Stylist View: Display 3D Cranial Solid face with chosen hairstyle
          setUser3D({
            ...userData,
            cranialModel: userData.biometricData?.cranialSolidUrl || userData.headScan
          });
        }
      }
    };

    if (id) {
      fetchBarber();
      fetchUserHeadScan();
    }
  }, [id, navigate]);

  // Fetch Reviews (Real-time)
  useEffect(() => {
    if (!id) return;
    const q = query(
      collection(db, 'reviews'),
      where('stylistId', '==', id),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [id]);

  // Fetch Personal Portfolio (Real-time)
  useEffect(() => {
    if (!id) return;
    const q = query(
      collection(db, 'barbers', id, 'portfolio'), 
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setPortfolio(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [id]);

  const handleDeleteWork = async (itemId, imageUrl) => {
    if (!window.confirm("Permanently remove this work from your portfolio?")) return;
    const loadingToast = toast.loading("Removing from gallery...", toastStyle);
    try {
      await deleteDoc(doc(db, 'barbers', id, 'portfolio', itemId));
      if (imageUrl) {
        const storageReference = ref(storage, imageUrl);
        await deleteObject(storageReference);
      }
      toast.success("Portfolio updated", { id: loadingToast, ...toastStyle });
    } catch (err) {
      toast.error("Delete failed. Try again.", { id: loadingToast, ...toastStyle });
    }
  };

  const startConsultation = async () => {
    if (!auth.currentUser) return toast.error("Please log in to chat.", toastStyle);
    const chatId = `${auth.currentUser.uid}_${barber.id}`;
    try {
      await setDoc(doc(db, 'chats', chatId), {
        participantIds: [auth.currentUser.uid, barber.id],
        lastUpdated: new Date(),
        stylistName: barber.shopName || barber.displayName || barber.name,
        clientName: auth.currentUser.displayName || 'Client',
        cranialSolidRef: user3D?.cranialModel || null
      }, { merge: true });
      setShowChat(true);
    } catch (err) {
      toast.error("Could not start consultation.", toastStyle);
    }
  };

  if (loading) return <div style={fullPageCenter}><h2 style={{color: GOLD}}>Calibrating 3D Interface...</h2></div>;
  if (!barber) return null;

  const ratings = barber.ratings || [];
  const averageRating = ratings.length > 0
      ? (ratings.reduce((sum, r) => sum + r, 0) / ratings.length).toFixed(1)
      : '5.0';

  const chatId = auth.currentUser ? `${auth.currentUser.uid}_${barber.id}` : null;

  return (
    <div style={profileContainer}>
      <div style={{ textAlign: 'center', margin: '2rem 0' }}>
        <img src="/icons/esdras-logo.png" alt="Esdras" style={{ width: 140, cursor: 'pointer' }} onClick={() => navigate('/')} />
        <h1 style={titleStyle}>{barber.shopName || barber.displayName || barber.name}</h1>
        <p style={subtitleStyle}>
            {barber.address || barber.location} • <span style={{ color: GOLD }}>★ {averageRating}</span> ({ratings.length}) • {barber.specialty || 'Professional Stylist'}
        </p>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <button onClick={startConsultation} style={consultBtnStyle}>💬 Start 3D Cranial Consultation</button>
      </div>

      {barber.reelVideoUrl && (
        <div style={videoContainerStyle}>
          <video src={barber.reelVideoUrl} autoPlay loop muted playsInline style={{ width: '100%', display: 'block' }} />
        </div>
      )}

      <h2 style={sectionHeaderStyle}>Signature Styles</h2>
      <div style={styleGridStyle}>
        {(barber.signatureStyles || []).map((style) => (
          <div
            key={style.id || style.name}
            onClick={() => setSelectedStyle(style)}
            style={{
              ...styleCardBase,
              border: (selectedStyle?.id === style.id || selectedStyle?.name === style.name) ? `4px solid ${GOLD}` : '4px solid transparent',
              transform: (selectedStyle?.id === style.id || selectedStyle?.name === style.name) ? 'scale(1.05)' : 'scale(1)'
            }}
          >
            <img src={style.image || '/placeholder-style.jpg'} style={styleImgStyle} alt={style.name} />
            <p style={styleNameStyle}>{style.name} {style.price ? `- ₦${style.price}` : ''}</p>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center', margin: '4rem 0' }}>
        <button
          onClick={() => selectedStyle && setShowBooking(true)}
          disabled={!selectedStyle}
          style={{
            ...bookBtnBase,
            background: selectedStyle ? GOLD : '#555',
            opacity: selectedStyle ? 1 : 0.6,
            cursor: selectedStyle ? 'pointer' : 'not-allowed',
          }}
        >
          {selectedStyle ? `Book "${selectedStyle.name}" Now` : 'Select a Style to Book'}
        </button>
      </div>

      {portfolio.length > 0 && (
        <>
          <h2 style={sectionHeaderStyle}>Real Results</h2>
          <div style={portfolioGridStyle}>
            {portfolio.map(item => (
              <div key={item.id} style={portfolioCardStyle}>
                <img src={item.imageUrl} style={portfolioImgStyle} alt="work" />
                {isOwner && <button onClick={() => handleDeleteWork(item.id, item.imageUrl)} style={deleteBtnStyle}>REMOVE</button>}
                <div style={portfolioOverlayStyle}><p style={{ fontSize: '0.75rem', margin: 0 }}>{item.description}</p></div>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={reviewsSectionStyle}>
        <h3 style={feedbackTitleStyle}>Client Feedback</h3>
        {reviews.length === 0 ? <p style={{ opacity: 0.5, textAlign: 'center' }}>No reviews yet.</p> : 
          reviews.map(review => (
            <div key={review.id} style={reviewCardStyle}>
              <div style={reviewHeaderRow}>
                <div>
                  <span style={{ fontWeight: 'bold', color: GOLD }}>{review.clientName}</span>
                  {review.bookingId && review.bookingId !== 'manual_entry' && <VerifiedBadge />}
                </div>
                <span style={{ color: GOLD, letterSpacing: '2px' }}>{'★'.repeat(review.stars)}<span style={{ color: '#444' }}>{'★'.repeat(5 - review.stars)}</span></span>
              </div>
              <p style={reviewTextStyle}>"{review.comment}"</p>
              <div style={{ fontSize: '0.65rem', opacity: 0.4, marginTop: '10px' }}>
                {review.createdAt?.toDate ? review.createdAt.toDate().toLocaleDateString() : 'Recent'}
              </div>
            </div>
          ))
        }
      </div>

      {showBooking && selectedStyle && (
        <BookingModal
          barber={barber}
          monnifySubAccountCode={barber.monnifySubAccountCode} // CRITICAL: Routing for 90/10 payment split
          styleName={selectedStyle.name}
          stylePrice={selectedStyle.price}
          renderedImageUrl={selectedStyle.image}
          userHeadScan={user3D?.cranialModel} // Stylist view requirement
          onClose={() => setShowBooking(false)}
        />
      )}

      {showChat && <Chat chatId={chatId} stylistId={barber.id} cranialModel={user3D?.cranialModel} onClose={() => setShowChat(false)} />}
    </div>
  );
}

// --- STYLESHEET ---
const profileContainer = { minHeight: '100vh', background: NAVY, color: 'white', fontFamily: 'Montserrat, sans-serif', padding: '2rem 1rem' };
const fullPageCenter = { minHeight: '100vh', background: NAVY, display: 'grid', placeItems: 'center', fontFamily: 'Montserrat, sans-serif' };
const titleStyle = { fontSize: '2.8rem', fontWeight: '800', color: GOLD, margin: '0.5rem 0' };
const subtitleStyle = { fontSize: '1.2rem', opacity: 0.9 };
const sectionHeaderStyle = { textAlign: 'center', color: GOLD, fontSize: '2.2rem', margin: '4rem 0 2rem', fontWeight: '800' };
const consultBtnStyle = { background: 'transparent', color: GOLD, border: `2px solid ${GOLD}`, padding: '14px 28px', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1.1rem', transition: '0.3s' };
const videoContainerStyle = { maxWidth: '540px', margin: '0 auto', borderRadius: '28px', overflow: 'hidden', border: `5px solid ${GOLD}`, boxShadow: '0 20px 40px rgba(0,0,0,0.5)' };
const styleGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', maxWidth: '1000px', margin: '0 auto' };
const styleCardBase = { cursor: 'pointer', textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '20px', overflow: 'hidden', transition: 'all 0.3s' };
const styleImgStyle = { width: '100%', height: '240px', objectFit: 'cover' };
const styleNameStyle = { margin: '1rem 0', fontWeight: 'bold', fontSize: '1.1rem' };
const bookBtnBase = { color: 'black', padding: '1.6rem 4rem', borderRadius: '50px', border: 'none', fontSize: '1.2rem', fontWeight: 'bold', boxShadow: '0 10px 25px rgba(184,134,11,0.3)', cursor: 'pointer' };
const portfolioGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', maxWidth: '1000px', margin: '0 auto' };
const portfolioCardStyle = { position: 'relative', borderRadius: '15px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', height: '250px' };
const portfolioImgStyle = { width: '100%', height: '100%', objectFit: 'cover' };
const portfolioOverlayStyle = { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', padding: '15px', color: 'white' };
const deleteBtnStyle = { position: 'absolute', top: '10px', right: '10px', background: '#ff4d4d', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '8px', fontSize: '0.6rem', fontWeight: 'bold', cursor: 'pointer', zIndex: 5 };
const reviewsSectionStyle = { maxWidth: '800px', margin: '5rem auto', padding: '0 1rem' };
const feedbackTitleStyle = { color: GOLD, borderBottom: `1px solid rgba(184,134,11,0.3)`, paddingBottom: '10px', marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '10px' };
const reviewCardStyle = { background: 'rgba(255,255,255,0.02)', padding: '25px', borderRadius: '20px', marginBottom: '20px', border: '1px solid rgba(184,134,11,0.1)' };
const reviewHeaderRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' };
const reviewTextStyle = { margin: 0, fontSize: '0.9rem', lineHeight: '1.5', opacity: 0.9, fontStyle: 'italic' };
